import { LoaderIcon } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useMemo, useRef } from 'react';

import { useElementSize } from '../../shared/hooks/useElementSize';
import type { VideoBroadcastWidgetState } from '../store/VideoBroadcastWidgetState';
import { camerasGridPropertiesCalculator } from '../utils';
import { VideoTooltipContext } from '../VideoTooltipContext';

import { Carousel } from './Carousel';
import { SidebarVideo } from './Sidebar';
import { VideoBroadcastPlaceholder } from './VideoBroadcastPlaceholder';
import styles from './VideoBroadcastWidget.module.scss';

type VideoBroadcastWidgetProps = {
  widgetState: VideoBroadcastWidgetState;
  externalContainer?: HTMLElement;
};

export const VideoBroadcastWidget = observer(function VideoBroadcastWidget({
  widgetState,
  externalContainer,
}: VideoBroadcastWidgetProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  const { width: bodyWidth, height: bodyHeight } = useElementSize(bodyRef);
  const gridWidth = Math.max(1, bodyWidth - 12 * 2);
  const gridHeight = Math.max(1, bodyHeight - 25);
  const { rows, columns, cameraHeight, cameraWidth } = useMemo(() => {
    return camerasGridPropertiesCalculator(
      widgetState.aspectRatio,
      { gridW: gridWidth, gridH: gridHeight },
      widgetState.selectedCamerasQuantity,
    );
  }, [gridHeight, gridWidth, widgetState.aspectRatio, widgetState.selectedCamerasQuantity]);

  return (
    <VideoTooltipContext.Provider value={externalContainer}>
      <div className={clsx(styles.root, `go-widget-theme-${widgetState.appParams.theme}`)}>
        <div className={styles.gridWrapper} ref={bodyRef} data-ef>
          {widgetState.isCamerasLoading ? (
            <LoaderIcon size="l" />
          ) : widgetState.cameras.length ? (
            <Carousel
              widgetState={widgetState}
              cameraHeight={cameraHeight}
              cameraWidth={cameraWidth}
              columns={columns}
              onVisibleCamerasChange={widgetState.setVisibleCameras}
              rows={rows}
              selectedCameras={widgetState.selectedCameras}
            />
          ) : (
            <VideoBroadcastPlaceholder />
          )}
        </div>

        <div className={styles.sidebarWrapper}>
          <SidebarVideo widgetState={widgetState} />
        </div>
      </div>
    </VideoTooltipContext.Provider>
  );
});
